import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Container, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { Megaphone as MegaphoneIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';

import { getAnnouncement } from '@/api/announcements';
import { paths } from '@/paths';
import { Seo } from '@/components/seo';

import { AnnouncementHeader } from './components/announcement-header';
import { AnnouncementBody, AnnouncementSidebar } from './components/announcement-details-card';

export default function AnnouncementPage(): React.JSX.Element {
  const { announcementId } = useParams();
  const theme = useTheme();

  const { data: announcement, isLoading, error, refetch } = useQuery({
    queryKey: ['announcement', announcementId],
    queryFn: () => getAnnouncement(announcementId!),
    retry: false,
    // Poll every 4 s only when the backend is actively processing an immediate
    // send. For scheduled announcements that haven't fired yet, polling would
    // run indefinitely for no reason — so we skip it when scheduled_at is in
    // the future. Once the scheduled time passes, the next manual visit /
    // refetch will reflect the final status.
    refetchInterval: (query) => {
      const data = query.state.data as { status?: string; scheduled_at?: string | null } | undefined;
      const status = data?.status?.toLowerCase();
      if (status !== 'pending' && status !== 'processing') return false;
      const scheduledAt = data?.scheduled_at;
      if (scheduledAt && new Date(scheduledAt) > new Date()) return false;
      return 4000;
    },
  });

  if (!isLoading && error) {
    return (
      <>
        <Seo title="Announcement Not Found" />
        <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
          <Container maxWidth="xl" sx={{ py: 10, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <MegaphoneIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                Announcement not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This announcement doesn't exist or you don't have access to it.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => window.history.back()} variant="outlined">Go Back</Button>
                <Button href={paths.announcements.index} variant="contained">All Announcements</Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title={announcement?.subject ?? 'Announcement'} />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <AnnouncementHeader announcement={announcement} isLoading={isLoading} />
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4}>
            {/* ── Left: message content ── */}
            <Grid md={8} xs={12}>
              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                <Box sx={{ p: 3 }}>
                  <AnnouncementBody
                    announcement={announcement}
                    isLoading={isLoading}
                    onRefresh={() => refetch()}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* ── Right: sidebar ── */}
            <Grid md={4} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: { md: 'sticky' },
                  top: { md: 24 },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <AnnouncementSidebar announcement={announcement} isLoading={isLoading} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
